// URL Safety
// ----------
// Single source of truth on the frontend for "is this URL safe to render
// as an active, navigable link?" Mirrors the backend predicate
// (nimis-intelligence-backend/models/url_safety.py::is_safe_http_url) so
// the two agree exactly.
//
// ROOT-CAUSE INTENT (see SECURITY_AUDIT_FINDINGS F1/F2)
// ----------------------------------------------------
// A pentest report must be able to CAPTURE and DISPLAY dangerous URLs — a
// discovered `javascript:` / `data:` reference is legitimate evidence.
// React does NOT sanitise `href`, so binding an unvalidated URL to an
// anchor lets a stored `javascript:` URL execute on click. We never strip
// or reject the value (that would destroy evidence); instead we classify
// it and render non-http(s) URLs as inert, escaped text.
//
// The backend already computes an authoritative `referenceLinkNavigable`
// flag on each SubFinding. This function is the client-side defence in
// depth: a renderer must never emit a live link from an unvalidated
// string, even one the server vouched for. Callers gate on BOTH.

// Only these schemes may be rendered as a live <a href>. Everything else
// (javascript:, data:, vbscript:, file:, mailto:, scheme-relative,
// relative, empty) is treated as non-navigable and shown as text.
const NAVIGABLE_URL_PROTOCOLS: readonly string[] = ["http:", "https:"];

// Returns true only when `candidate` is an absolute http/https URL.
//
// The WHATWG `URL` parser canonicalises the input before we inspect the
// protocol: it strips the tab/newline/CR and leading control characters
// that browsers ignore in URLs (so `java\tscript:` and `\x00javascript:`
// both resolve to the `javascript:` protocol and are rejected), lowercases
// the scheme, and throws on relative/scheme-less references — which we
// treat as not-navigable. This closes the parser-differential bypasses
// that a hand-rolled prefix check would miss.
export function isSafeHttpUrl(candidate: string | null | undefined): boolean {
  if (!candidate || typeof candidate !== "string") {
    return false;
  }

  let parsedUrl: URL;
  try {
    parsedUrl = new URL(candidate.trim());
  } catch {
    // Relative, scheme-less, or otherwise unparseable — not a live link.
    return false;
  }

  return NAVIGABLE_URL_PROTOCOLS.includes(parsedUrl.protocol);
}

// Matches an explicit URI scheme prefix ("https:", "javascript:") so a
// schemed URL can be told apart from a bare host ("example.com"). Mirrors
// the backend _URL_SCHEME_PREFIX (models/url_safety.py).
const URL_SCHEME_PREFIX = /^[a-zA-Z][a-zA-Z0-9+.-]*:/;

// Characters browsers ignore inside a URL — stripped before scheme
// detection so `java\tscript:` is caught. Mirrors the backend.
// eslint-disable-next-line no-control-regex
const URL_IGNORED_CHARACTERS = /[\t\r\n ]/g;

// True when `rawUrl` carries an EXPLICIT scheme that is not http/https
// (javascript:, data:, file:, …). Bare hosts (no scheme) return false so a
// scheme can still be auto-prepended. Used to reject a scan-target URL
// before `addProtocolIfMissing` masks the scheme by prepending https://.
// Mirrors the backend normalise_scan_target_url reject path (F3).
export function hasDisallowedUrlScheme(rawUrl: string | null | undefined): boolean {
  if (!rawUrl || typeof rawUrl !== "string") {
    return false;
  }
  const cleaned = rawUrl.trim().replace(URL_IGNORED_CHARACTERS, "");
  const schemeMatch = cleaned.match(URL_SCHEME_PREFIX);
  if (!schemeMatch) {
    return false;
  }
  const scheme = schemeMatch[0].slice(0, -1).toLowerCase();
  return scheme !== "http" && scheme !== "https";
}

// Resolves the final render decision for a SubFinding reference link.
//
// The server flag is authoritative when it explicitly says "not
// navigable" (never override a server block). Otherwise — flag true, or
// absent on a legacy/unenriched payload — the client allowlist is the
// gate. A link is rendered only when BOTH the server did not veto it and
// the URL passes the client allowlist.
export function referenceLinkIsNavigable(
  referenceLink: string | null | undefined,
  serverNavigableFlag: boolean | null | undefined,
): boolean {
  if (serverNavigableFlag === false) {
    return false;
  }
  return isSafeHttpUrl(referenceLink);
}
